<!-- Thin orange vertical bar on the calendar right edge showing context time spans. -->
<template>
  <button
    v-for="ctx in visibleContexts"
    :key="ctx.id"
    type="button"
    class="context-bar"
    :class="{ 'context-bar--active': ctx.running }"
    :style="barStyle(ctx)"
    @mouseenter="showTooltip(ctx)"
    @mouseleave="hideTooltip"
    @mousemove="updateTooltipPosition"
    @click.stop="$emit('context-click', ctx)"
  >
    <span class="context-bar__line"></span>
  </button>

  <div
    v-if="isTooltipVisible && tooltipDetails"
    class="drag-tooltip"
    :style="{ left: tooltipPosition.x + 'px', top: tooltipPosition.y + 'px' }"
  >
    <p class="drag-tooltip__label">{{ tooltipDetails.label }}</p>
    <p>{{ tooltipDetails.timeRange }}</p>
    <p>{{ tooltipDetails.duration }}</p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useBlocksStore } from '../stores/blocks'
import { formatDuration, formatLocalizedTime } from '../utils/time'

defineEmits(['context-click'])

const props = defineProps({
  displayStart: { type: Date, required: true },
})

const store = useBlocksStore()
const isTooltipVisible = ref(false)
const tooltipPosition = ref({ x: 0, y: 0 })
const tooltipTimeout = ref(null)
const hoveredContext = ref(null)

const visibleContexts = computed(() => {
  return (store.recentContexts || []).filter(ctx => {
    const start = new Date(ctx.started_at)
    const end = ctx.stopped_at ? new Date(ctx.stopped_at) : store.currentTime
    const displayEnd = new Date(props.displayStart.getTime() + 30 * 60 * 60 * 1000)
    return end > props.displayStart && start < displayEnd
  })
})

const tooltipDetails = computed(() => {
  if (!hoveredContext.value) return null

  const start = new Date(hoveredContext.value.started_at)
  const end = hoveredContext.value.stopped_at
    ? new Date(hoveredContext.value.stopped_at)
    : store.currentTime
  const endLabel = hoveredContext.value.running ? 'Running...' : formatLocalizedTime(end)

  // Running contexts carry a stale duration_seconds snapshot; derive it live from currentTime.
  const durationSeconds = hoveredContext.value.running
    ? Math.max(0, Math.floor((end - start) / 1000))
    : hoveredContext.value.duration_seconds

  return {
    label: hoveredContext.value.display_label,
    timeRange: `${formatLocalizedTime(start)} - ${endLabel}`,
    duration: formatDuration(durationSeconds),
  }
})

function barStyle(ctx) {
  const SLOT_HEIGHT = 10
  const GRID_TOP_OFFSET = 15
  const start = new Date(ctx.started_at)
  const end = ctx.stopped_at ? new Date(ctx.stopped_at) : store.currentTime
  const displayEnd = new Date(props.displayStart.getTime() + 30 * 60 * 60 * 1000)

  const clampedStart = start < props.displayStart ? props.displayStart : start
  const clampedEnd = end > displayEnd ? displayEnd : end

  const startMinutesFromDisplayStart = (clampedStart - props.displayStart) / (1000 * 60)
  const endMinutesFromDisplayStart = (clampedEnd - props.displayStart) / (1000 * 60)

  const topPosition = (startMinutesFromDisplayStart / 5) * SLOT_HEIGHT + GRID_TOP_OFFSET
  const endPosition = (endMinutesFromDisplayStart / 5) * SLOT_HEIGHT + GRID_TOP_OFFSET
  const height = Math.max(endPosition - topPosition, 2)

  return {
    position: 'absolute',
    top: `${topPosition}px`,
    right: '0',
    width: '12px',
    height: `${height}px`,
    zIndex: 8,
  }
}

function showTooltip(ctx) {
  hoveredContext.value = ctx

  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
  }

  tooltipTimeout.value = setTimeout(() => {
    isTooltipVisible.value = true
  }, 250)
}

function hideTooltip() {
  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
    tooltipTimeout.value = null
  }

  isTooltipVisible.value = false
  hoveredContext.value = null
}

function updateTooltipPosition(event) {
  tooltipPosition.value = {
    x: event.clientX + 10,
    y: event.clientY + 10,
  }
}
</script>

<style scoped>
.context-bar {
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  transition: background 0.15s;
}

.context-bar__line {
  width: 3px;
  height: 100%;
  background: rgba(213, 99, 45, 0.5);
  border-radius: 1.5px;
  transition: background 0.15s;
}

.context-bar:hover .context-bar__line {
  background: rgba(213, 99, 45, 0.75);
}

.context-bar--active .context-bar__line {
  background: #d5632d;
}

.context-bar--active:hover .context-bar__line {
  background: #c0562a;
}

.drag-tooltip {
  position: fixed;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: pre-line;
}

.drag-tooltip p {
  margin: 0;
}

.drag-tooltip__label {
  font-weight: 700;
}
</style>
